import { useEffect, useMemo, useRef, type PointerEvent, type RefObject } from "react";
import { DragGesture } from "../drag/gesture.ts";
import { placeGhost } from "../drag/ghost.tsx";
import { dropOf, dropTargetAt } from "../drag/targets.ts";
import type { BarArranging } from "./arranging.ts";
import { SWAP_TARGET } from "./page-follow.ts";
import { SwapHold, overSwapControl } from "./swap-hold.ts";
import type { TipController } from "./tips.ts";

/**
 * Dragging the bar's slots about (P2.8-T29, ADR-121 decision 3.1), on the bag's gesture (`drag/gesture.ts`, P2.8-T14):
 * a press on a slot holding something becomes a drag once it moves more than 8 CSS pixels inside 500 ms; a press held
 * still pins its tooltip and a press released without moving uses the slot, as before. With the lock on, the press
 * must hold Shift. Released over another slot of the bar the two swap; released anywhere else an item's slot clears
 * and an ability or a blow is put back (ADR-121's note of 2026-10-10), a release over the world included, which the
 * world never sees as a press of its own, since the press began on the bar. Where the bar pages, a drag held over
 * the swap control for 500 ms shows the other page (P2.8-T30, `swap-hold.ts`), the control drawn as a slot that takes
 * the drop while the drag rests on it; one released on the control changes nothing, and that release and every
 * cancelled drag turn the bar back to the page the drag started on. Escape, a lost window and a cancelled
 * pointer end the drag with nothing changed. A touch never drags, so a long press keeps its tooltip. The click that
 * follows a drop is swallowed, so a drop never uses the slot it lands on. The ghost follows the pointer, placed on
 * pointer events only, so no frame allocates.
 */

/**
 * What the bar draws a drag with: the press handler for its slots, the ghost's element, whether the click now arriving
 * ends a drop and must be swallowed, and a way to place the ghost as it is first drawn.
 */
export interface BarDrag {
  readonly onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  readonly ghost: RefObject<HTMLDivElement | null>;
  readonly swallow: () => boolean;
  /**
   * Places the ghost at the pointer's last place, as the ghost is first drawn.
   */
  readonly placeNow: () => void;
}

/**
 * Drags slots of the bar about.
 *
 * @param arranging The page's arranging, or null where nothing arranges the bar.
 * @param tips The page's tooltip controller, whose tooltip a drag drops, or null.
 * @returns What the bar draws the drag with.
 */
export function useBarDrag(arranging: BarArranging | null, tips: Pick<TipController, "pressCancel" | "clear"> | null): BarDrag {
  const gesture = useMemo(() => new DragGesture(), []);
  const ghost = useRef<HTMLDivElement>(null);
  const swallowed = useRef(false);
  const shift = useRef(false);
  const pointer = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (arranging === null) {
      return undefined;
    }
    /**
     * The bar slot under a point, or null off the bar.
     *
     * @param x The point's x.
     * @param y The point's y.
     * @returns The slot, from 0.
     */
    const barAt = (x: number, y: number): number | null => {
      if (overSwapControl(x, y)) {
        return SWAP_TARGET;
      }
      const target = dropTargetAt(x, y);
      return target !== null && target.surface === "bar" ? target.index : null;
    };
    const hold = new SwapHold(() => arranging.follow.turnForDrag());
    /**
     * Follows the pointer: starts the drag once the press has moved far enough in time, then moves the ghost and marks
     * the slot under the pointer.
     *
     * @param event The move.
     */
    const moved = (event: globalThis.PointerEvent): void => {
      pointer.current.x = event.clientX;
      pointer.current.y = event.clientY;
      if (gesture.move(event.pointerId, event.clientX, event.clientY, performance.now())) {
        const source = gesture.active;
        if (source === null || !arranging.pick(source.index, shift.current)) {
          gesture.cancel();
          return;
        }
        tips?.pressCancel();
        tips?.clear();
      }
      if (gesture.active !== null) {
        placeGhost(ghost.current, event.clientX, event.clientY);
        const over = barAt(event.clientX, event.clientY);
        arranging.hoverFromBar(over);
        hold.over(over === SWAP_TARGET);
      }
    };
    /**
     * Drops the drag where the pointer is released, and swallows the click that follows.
     *
     * @param event The release.
     */
    const released = (event: globalThis.PointerEvent): void => {
      if (gesture.release(event.pointerId) === null) {
        return;
      }
      hold.end();
      const over = barAt(event.clientX, event.clientY);
      if (over === SWAP_TARGET) {
        arranging.cancelDrag();
      } else {
        arranging.dropFromBar(over);
      }
      swallowed.current = true;
      setTimeout(() => {
        swallowed.current = false;
      }, 0);
    };
    /**
     * Ends a drag with nothing changed.
     */
    const cancelled = (): void => {
      hold.end();
      if (gesture.cancel()) {
        arranging.cancelDrag();
      }
    };
    /**
     * Escape ends a drag before anything else hears it.
     *
     * @param event The key.
     */
    const keyed = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && gesture.active !== null) {
        event.preventDefault();
        event.stopImmediatePropagation();
        cancelled();
      }
    };
    window.addEventListener("pointermove", moved);
    window.addEventListener("pointerup", released);
    window.addEventListener("pointercancel", cancelled);
    window.addEventListener("blur", cancelled);
    window.addEventListener("keydown", keyed, { capture: true });
    return () => {
      window.removeEventListener("pointermove", moved);
      window.removeEventListener("pointerup", released);
      window.removeEventListener("pointercancel", cancelled);
      window.removeEventListener("blur", cancelled);
      window.removeEventListener("keydown", keyed, { capture: true });
      hold.end();
      if (gesture.cancel()) {
        arranging.cancelDrag();
      }
    };
  }, [gesture, arranging, tips]);

  return useMemo(
    (): BarDrag => ({
      onPointerDown: (event) => {
        if (arranging === null || event.button !== 0 || event.pointerType === "touch" || !arranging.canArrange()) {
          return;
        }
        const anchor = dropOf(event.target as Element);
        if (anchor === null || anchor.surface !== "bar" || arranging.thingAt(anchor.index) === null) {
          return;
        }
        shift.current = event.shiftKey;
        gesture.press(anchor, event.pointerId, event.clientX, event.clientY, performance.now());
      },
      ghost,
      swallow: () => swallowed.current,
      placeNow: () => placeGhost(ghost.current, pointer.current.x, pointer.current.y),
    }),
    [arranging, gesture],
  );
}
